import { IMAGES } from "@/lib/config";
import useReveal from "@/hooks/useReveal";

const ITEMS = [
    {
        title: "Longère normande — façade chaux",
        location: "Eure-et-Loir",
        year: "2024",
        tag: "Enduits",
        img: IMAGES.gallery1,
        alt: "Restauration de façade en pierre d'une longère normande avec enduit à la chaux aérienne — Eure-et-Loir",
        size: "tall",
    },
    {
        title: "Plâtrerie traditionnelle — reprise d'enduit",
        location: "Yvelines",
        year: "2023",
        tag: "Plâtrerie",
        img: IMAGES.texture,
        alt: "Travaux de plâtrerie traditionnelle — application d'un enduit au plâtre sur un mur ancien — Yvelines",
        size: "wide",
    },
    {
        title: "Diagnostic humidité maison du XVIIIᵉ",
        location: "Perche",
        year: "2024",
        tag: "Diagnostic",
        img: IMAGES.hero,
        alt: "Diagnostic des pathologies d'humidité sur les murs en pierre d'une maison du XVIIIᵉ siècle — Perche",
        size: "regular",
    },
    {
        title: "Atelier formation chaux & terre",
        location: "Eure-et-Loir",
        year: "2025",
        tag: "Formation",
        img: IMAGES.training,
        alt: "Atelier de formation pratique aux enduits chaux et terre crue pour le bâti ancien — Eure-et-Loir",
        size: "regular",
    },
    {
        title: "Décoration intérieure — peinture & finitions",
        location: "Eure-et-Loir",
        year: "2024",
        tag: "Peinture",
        img: IMAGES.painting,
        alt: "Intérieur décoré avec peintures compatibles bâti ancien — finitions artisanales et décoration sensible",
        size: "regular",
    },
];

export default function Gallery() {
    const ref = useReveal();
    return (
        <section
            id="realisations"
            data-testid="gallery-section"
            ref={ref}
            className="relative py-24 md:py-32 bg-sand/30 border-y border-border/60"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="label-overline text-brand">Réalisations</span>
                        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                            Quelques chantiers,
                            <br />
                            <span className="italic">beaucoup d'attention.</span>
                        </h2>
                    </div>
                    <p className="text-stone2 max-w-md text-sm md:text-base">
                        De la longère oubliée au mur de soutènement effondré : chaque
                        chantier commence par une écoute et un diagnostic, jamais par une recette.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
                    {ITEMS.map((it, idx) => {
                        const span =
                            it.size === "tall"
                                ? "md:col-span-3 md:row-span-2 aspect-[4/5]"
                                : it.size === "wide"
                                ? "md:col-span-3 aspect-[16/10]"
                                : "md:col-span-3 aspect-[16/10]";
                        return (
                            <figure
                                key={idx}
                                data-testid={`gallery-item-${idx}`}
                                className={`reveal group relative overflow-hidden rounded-sm border border-border bg-card ${span}`}
                                style={{ transitionDelay: `${idx * 90}ms` }}
                            >
                                <img
                                    src={it.img}
                                    alt={it.alt}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                                <div className="absolute top-4 left-4">
                                    <span className="label-overline text-cream bg-brand/90 px-3 py-1 rounded-sm">
                                        {it.tag}
                                    </span>
                                </div>
                                <figcaption className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-cream">
                                    <div className="font-heading text-lg md:text-xl font-medium tracking-tight">
                                        {it.title}
                                    </div>
                                    <div className="text-xs md:text-sm opacity-90 mt-1">
                                        {it.location} · {it.year}
                                    </div>
                                </figcaption>
                            </figure>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
